import clsx from 'clsx';
import { useT } from '../i18n';
import type { MentionItem, MentionKind } from '../lib/mentions';
import { MenuIcon, type MenuIconName } from './MenuIcon';

const HEADING: Record<
  MentionKind,
  'mentionSkills' | 'mentionKnowledge' | 'mentionDocuments' | 'mentionMicrosoft'
> = {
  skill: 'mentionSkills',
  knowledge: 'mentionKnowledge',
  document: 'mentionDocuments',
  microsoft: 'mentionMicrosoft',
};
const ICON: Record<MentionKind, MenuIconName> = {
  skill: 'sparkles',
  knowledge: 'database',
  document: 'file',
  microsoft: 'cloud',
};

export function mentionOptionId(listId: string, index: number): string {
  return `${listId}-${index}`;
}

/** The "@" list above the chat input; the input keeps focus and drives it with the arrow keys. */
export function MentionMenu({
  id,
  items,
  activeIndex,
  onHover,
  onPick,
}: {
  id: string;
  items: MentionItem[];
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (item: MentionItem) => void;
}) {
  const t = useT();
  return (
    <div
      className="nv-mention absolute right-3 bottom-[calc(100%+8px)] left-3 z-20 overflow-y-auto"
      style={{ maxHeight: 'min(50dvh, 24rem)' }}
    >
      {items.length === 0 ? (
        <p className="nv-mention-empty" role="status">
          {t('mentionNone')}
        </p>
      ) : (
        <div id={id} role="listbox" aria-label={t('mentionList')}>
          {items.map((item, index) => {
            const first = index === 0 || items[index - 1]?.kind !== item.kind;
            return (
              <div key={`${item.kind}:${item.key}`} role="presentation">
                {first && (
                  <div className="nv-mention-h" role="presentation">
                    {t(HEADING[item.kind])}
                  </div>
                )}
                <div
                  id={mentionOptionId(id, index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  className={clsx('nv-mention-it', index === activeIndex && 'nv-on')}
                  onMouseEnter={() => onHover(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onPick(item)}
                  onKeyDown={() => undefined}
                >
                  <MenuIcon name={ICON[item.kind]} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.active ? (
                    <span className="nv-mention-d">{t('mentionActive')}</span>
                  ) : (
                    item.detail && <span className="nv-mention-d truncate">{item.detail}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
